import { useEffect, useRef } from "react";
import { Radio } from "@/components/ui";
import type { PublicQuestion, Tone } from "@/lib/api/types";

export const TONES: Array<{ id: Tone; label: string; hint: string }> = [
  { id: "calm", label: "담백하게", hint: "있었던 일을 차분하게 적어요" },
  { id: "bright", label: "가볍게", hint: "조금 더 밝은 말투로 적어요" },
];

/** Step 2: template questions as radio rows, then tone. */
export function StepQuestions({
  questions,
  answers,
  tone,
  blockedQuestion,
  name,
  onAnswer,
  onTone,
}: {
  questions: PublicQuestion[];
  answers: Record<string, string>;
  tone: Tone;
  blockedQuestion: string | null;
  /** Prefix for radio group names, unique per mounted flow. */
  name: string;
  onAnswer: (questionId: string, optionId: string) => void;
  onTone: (tone: Tone) => void;
}) {
  const refs = useRef<Record<string, HTMLFieldSetElement | null>>({});

  // A required question blocked "next": move focus to its first option.
  useEffect(() => {
    if (!blockedQuestion) return;
    refs.current[blockedQuestion]?.querySelector<HTMLInputElement>("input")?.focus();
  }, [blockedQuestion]);

  return (
    <div className="mt-4 flex flex-col gap-8">
      {questions.map((q) => (
        <fieldset
          key={q.id}
          ref={(el) => {
            refs.current[q.id] = el;
          }}
          aria-invalid={blockedQuestion === q.id || undefined}
        >
          <legend className="wds-headline-1 text-label-normal">
            {q.prompt}{" "}
            <span className="text-body-1 font-medium text-label-neutral">
              {q.required ? "(꼭 골라 주세요)" : "(선택)"}
            </span>
          </legend>
          <div className="mt-3 flex flex-col gap-2">
            {q.options.map((o) => (
              <Radio
                key={o.id}
                variant="row"
                name={`${name}-${q.id}`}
                checked={answers[q.id] === o.id}
                onChange={() => onAnswer(q.id, o.id)}
                label={o.label}
              />
            ))}
          </div>
        </fieldset>
      ))}
      <fieldset>
        <legend className="wds-headline-1 text-label-normal">어떤 말투가 좋아요?</legend>
        <div className="mt-3 flex flex-col gap-2">
          {TONES.map((t) => (
            <Radio
              key={t.id}
              variant="row"
              name={`${name}-tone`}
              checked={tone === t.id}
              onChange={() => onTone(t.id)}
              label={t.label}
              description={t.hint}
            />
          ))}
        </div>
      </fieldset>
    </div>
  );
}
